import React, { FormEvent, memo, useCallback, useEffect, useRef, useState } from 'react';
import { TweenLite } from 'gsap';
import { UseFormMethods } from 'react-hook-form';
import RHFCheckbox from '../controls/tempRHFControls/RHFCheckBox';
import ProvisionClauseType from 'src/models/ProvisionClauseType';
import ProvisionLeaseClause from 'src/models/ProvisionLeaseClause';
import RHFSelect from '../controls/tempRHFControls/RHFSelect';
import RHFInputBox from '../controls/tempRHFControls/RHFInputBox';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFLegend from '../controls/tempRHFControls/RHFLegend';
import { isArray } from 'lodash';

const responseOptions = [
    { value: 'Not Reviewed', label: 'Not Reviewed'},
    { value: 'Yes', label: 'Yes'},
    { value: 'No', label: 'No'}
];

const frequencyOptions = [
    { value: 'One-Time', label: 'One-Time'},
    { value: 'Monthly', label: 'Monthly'},
    { value: 'Yearly', label: 'Yearly'}
];

interface IProvisionClauseProps extends Partial<Pick<UseFormMethods, 'register' | 'errors' | 'getValues' | 'setValue'>> {
    attachedProvisionClauses: ProvisionLeaseClause[];
    options: ProvisionClauseType[];
    formPrefix: string;
    key?: string;
}

interface IProvisionClauseRowProps {
    pc: ProvisionLeaseClause;
    index: number;
    formKey: string;
    formPrefix: string;
    typeOptions: { value: string | number; label: string }[];
    allOptions: ProvisionClauseType[];
    register: UseFormMethods['register'];
    errors: UseFormMethods['errors'];
    setValue: UseFormMethods['setValue'];
    onEditClick: (index: number) => void;
    handleRemoveProvision: (index: number) => void;
    provLanguageEntry: string;
}

const ProvisionClauseRowInner = (props: IProvisionClauseRowProps) => {
    const { pc, index, formPrefix, typeOptions, allOptions, register, errors } = props;
    const languageRef = useRef(null);

    const [selectedTypeId, setSelectedTypeId] = useState<string>(pc.clauseTypeId?.toString() ?? '');
    const [omitObligation, setOmitObligation] = useState<boolean>(false);

    const isAttached = !!(pc.contractAddendumClauseId ?? pc.contractAdHocClauseId);
    const currentType = allOptions?.find(ot => ot.id.toString() === selectedTypeId);
    const valueOptions = currentType?.provisionValues?.map(ov => ({ value: ov.name, label: ov.name })) ?? [];
    const unitOptions = currentType?.provisionUnits?.map(ou => ({ value: ou.name, label: ou.name })) ?? [];

    useEffect(() => {
        const sType = parseInt(selectedTypeId, 10);
        const provType = sType > 0 ? allOptions?.find((ao) => ao.id === sType) : undefined;
        setOmitObligation(provType?.omitObligation === true);
    }, [selectedTypeId]);

    const handleTypeChange = (e: FormEvent<HTMLSelectElement>) => {
        setSelectedTypeId(e.currentTarget.value);
        props.setValue(`${formPrefix}[${index}].unit`, null);
        props.setValue(`${formPrefix}[${index}].value`, null);
    };

    const toggleLanguage = (state: boolean, clientX: number, clientY: number) => {
        if (state) {
            TweenLite.set(
                languageRef.current,
                { top: `${clientY - 10}`, left: `${clientX + 10}`, height: 'auto', display: 'inline-block', background: 'white', border: '2px solid #6393C1', zIndex: 100 }
            );
        } else {
            TweenLite.to(languageRef.current, 0.2, { height: '2.5rem', display: 'none', background: 'transparent', borderColor: 'transparent', zIndex: 0 });
        }
    };

    return (
        <div key={props.formKey} className="form-row mb-1">
            <div style={{ display: 'flex', borderBottom: '1px solid darkgrey', width: '100%' }}>
                <input type="hidden" name={`${formPrefix}[${index}].contractAddendumClauseId`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].contractAdHocClauseId`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].leaseAdHocClauseId`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].contractLookupId`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].id`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].language`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].shortDescription`} ref={register()} />
                <input type="hidden" name={`${formPrefix}[${index}].note`} ref={register()} />
                <div style={{ width: '12%' }} className="mr-1">
                    <RHFSelect
                        name={`${formPrefix}[${index}].clauseTypeId`}
                        register={register}
                        errors={errors}
                        options={typeOptions}
                        onChange={(e) => handleTypeChange(e)}
                        disabled={isAttached}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ width: '8%' }} className="mr-1">
                    <RHFSelect
                        name={`${formPrefix}[${index}].response`}
                        register={register}
                        errors={errors}
                        options={responseOptions}
                        defaultVal={'Not Reviewed'}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ width: '15%' }} className="mr-1">
                    <RHFSelect
                        name={`${formPrefix}[${index}].value`}
                        register={register}
                        placeholder={valueOptions.length === 0 ? '--N/A--' : '--SELECT--'}
                        errors={errors}
                        options={valueOptions}
                        disabled={valueOptions.length === 0}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ width: '15%' }} className="mr-1">
                    <RHFSelect
                        name={`${formPrefix}[${index}].unit`}
                        register={register}
                        placeholder={unitOptions.length === 0 ? '--N/A--' : '--SELECT--'}
                        errors={errors}
                        options={unitOptions}
                        disabled={unitOptions.length === 0}
                        style={{ width: '100%' }}
                    />
                </div>

                {!omitObligation && (
                    <>
                        <div style={{ width: '10%' }} className="mr-1">
                            <RHFInputBox
                                name={`${formPrefix}[${index}].obligationDate`}
                                type="date"
                                register={register}
                                errors={errors}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ width: '5%' }} className="mr-1">
                            <RHFInputBox
                                name={`${formPrefix}[${index}].frequency`}
                                type="number"
                                step={1}
                                register={register}
                                errors={errors}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ width: '7%' }} className="mr-1">
                            <RHFSelect
                                name={`${formPrefix}[${index}].frequencyPeriod`}
                                register={register}
                                errors={errors}
                                options={frequencyOptions}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <div style={{ width: '10%' }} className="mr-1">
                            <RHFInputBox
                                name={`${formPrefix}[${index}].obligationEndDate`}
                                type="date"
                                register={register}
                                errors={errors}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </>
                )}

                <div style={{ width: `${omitObligation ? '47%' : '15%'}` }} className="mr-1">
                    <div style={{display: 'flex'}}>
                        <span
                            style={{fontSize: '20px'}}
                            onMouseOver={(e) => toggleLanguage(true, e.clientX, e.clientY)}
                            onMouseOut={() => toggleLanguage(false, 0, 0)}
                        >
                            👁‍🗨
                        </span>
                        <button
                            type="button"
                            style={{ fontSize: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}
                            onClick={() => props.onEditClick(index)}
                            title="Edit Language"
                        >
                            ✏️
                        </button>
                        <span
                            style={{ textOverflow: 'ellipsis', overflow: 'hidden', display: 'inline-block', whiteSpace: 'nowrap' }}
                        >
                            {props.provLanguageEntry}
                        </span>
                        <span
                            ref={languageRef}
                            style={{
                                borderRadius: '5px',
                                border: '2px solid #6393C1',
                                display: 'none',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                position: 'absolute',
                                padding: '.3rem',
                                background: 'transparent',
                                borderColor: 'transparent',
                                zIndex: 10,
                                height: 'auto',
                            }}
                        >
                            {props.provLanguageEntry}
                        </span>
                    </div>
                </div>

                <div style={{ width: '3%' }}>
                    <RHFCheckbox
                        name={`${formPrefix}[${index}].useClientApproved`}
                        register={register}
                        errors={errors}
                        onClick={() => console.log('ADD CLIENT APPROVED LANGUAGE')}
                        disabled={!isAttached}
                    />
                </div>

                {!isAttached && (
                    <span
                        onClick={() => props.handleRemoveProvision(index)}
                        style={{ cursor: 'pointer', color: '#c23030', marginLeft: '1rem', fontWeight: 'bold' }}
                        title="Remove"
                    >
                        x
                    </span>
                )}
            </div>
        </div>
    );
};

const ProvisionClauseRow = memo(ProvisionClauseRowInner);

const ProvisionClause = (props: IProvisionClauseProps) => {
    const [typeOptions, setTypeOptions] = useState<{ value: string | number, label: string }[]>([]);
    const [formProvisions, setFormProvisions] = useState<ProvisionLeaseClause[]>(props.attachedProvisionClauses ?? []);
    const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(-1);
    const [selectedLanguage, setSelectedLanguage] = useState<string>('');
    const [selectedDescription, setselectedDescription] = useState<string>('');
    const [selectedNote, setselectedNote] = useState<string>('');
    const [provLanguage, setProvLanguage] = useState<string[]>([]);

    useEffect(() => {
        let tOptions = props.options?.map((ot) => {
            return { value: ot.id, label: ot.name };
        }) ?? [];
        setTypeOptions(tOptions);
    }, [props.options]);

    useEffect(() => {
        setFormProvisions(props.attachedProvisionClauses ?? []);
    }, [props.attachedProvisionClauses]);

    useEffect(() => {
        let language = [];
        if (isArray(formProvisions)) {
            for (let p of formProvisions) {
                language.push(p.language);
            }
        }
        setProvLanguage(language);
    }, [formProvisions]);

    const handleUpdateSelected = () => {
        let newLanguage = [...provLanguage];
        newLanguage[selectedRowIndex] = selectedLanguage;
        setProvLanguage(newLanguage);
        props.setValue(`${props.formPrefix}[${selectedRowIndex}].language`, selectedLanguage, { shouldDirty: true });
        props.setValue(`${props.formPrefix}[${selectedRowIndex}].shortDescription`, selectedDescription, { shouldDirty: true });
        props.setValue(`${props.formPrefix}[${selectedRowIndex}].note`, selectedNote, { shouldDirty: true });
        setSelectedRowIndex(-1);
    };

    const handleNewProvision = () => {
        let provList = [...formProvisions];
        provList.push(new ProvisionLeaseClause());
        setFormProvisions(provList);
    };

    const handleRemoveProvision = (index: number) => {
        let provList = [...formProvisions];
        provList.splice(index, 1);
        setFormProvisions(provList);
        props.setValue(props.formPrefix, provList, { shouldDirty: true });
    };

    const onEditClick = useCallback((index: number) => {
        setSelectedRowIndex(index);
        let rep = props.getValues();
        let indexedPath = `${props.formPrefix}[${index}]`;
        const fState = indexedPath
            .replace(/\[(\d+)\]/g, '.$1')
            .split('.')
            .reduce((obj, key) => obj?.[key], rep);

        setSelectedLanguage(fState.language);
        setselectedDescription(fState.shortDescription);
        setselectedNote(fState.note);
    }, [props.getValues, props.formPrefix]);

    return (
        <>
            {selectedRowIndex > -1 && (
                <div style={{ display: 'block', padding: '0.5rem 0' }}>
                    <RHFLegend title={typeOptions.find(to => to.value.toString() === props.getValues()?.[props.formPrefix]?.[selectedRowIndex]?.clauseTypeId)?.label ?? 'N/A'} />
                    <label htmlFor={'selectedLanguage'}>
                        Language
                    </label>
                    <br />
                    <textarea
                        style={{ width: '100%', height: 'auto', resize: 'vertical' }}
                        rows={5}
                        defaultValue={selectedLanguage}
                        onChange={(e) => setSelectedLanguage(e.currentTarget.value)}
                    />

                    <label htmlFor={'selectedShortDescription'}>
                        Short Description
                    </label>
                    <br />
                    <textarea
                        style={{ width: '100%', height: 'auto', resize: 'vertical' }}
                        rows={5}
                        defaultValue={selectedDescription}
                        onChange={(e) => setselectedDescription(e.currentTarget.value)}
                    />

                    <label htmlFor={'selectedNote'}>
                        Note
                    </label>
                    <br />
                    <textarea
                        style={{ width: '100%', height: 'auto', resize: 'vertical' }}
                        rows={5}
                        defaultValue={selectedNote}
                        onChange={(e) => setselectedNote(e.currentTarget.value)}
                    />
                    <Button text="Update Provision" intent="primary" onClick={handleUpdateSelected} />
                </div>
            )}
            <div style={{ display: selectedRowIndex === -1 ? '' : 'none' }}>
                {typeOptions.length > 0 && (
                    <>
                        <div className="form-row">
                            <div style={{ display: 'flex', width: '100%' }}>
                                <div style={{ width: '12%' }}>
                                    <span className="column-heading">Type</span>
                                </div>
                                <div style={{ width: '8%' }}>
                                    <span className="column-heading">Response</span>
                                </div>
                                <div style={{ width: '15%' }}>
                                    <span className="column-heading">Value</span>
                                </div>
                                <div style={{ width: '15%' }}>
                                    <span className="column-heading">Unit</span>
                                </div>
                                <div style={{ width: '10%' }}>
                                    <span className="column-heading">Obligation Date</span>
                                </div>
                                <div style={{ width: '12%' }}>
                                    <span className="column-heading">Obligation Frequency</span>
                                </div>
                                <div style={{ width: '10%' }}>
                                    <span className="column-heading">End Date</span>
                                </div>
                                <div style={{ width: '8%' }}>
                                    <span className="column-heading">Language</span>
                                </div>
                                <div style={{ width: '10%' }}>
                                    <span className="column-heading">Use Client Approved</span>
                                </div>
                            </div>
                        </div>

                        {formProvisions.map((pc: ProvisionLeaseClause, index: number) => (
                            <ProvisionClauseRow
                                key={`${props.key}-${index}`}
                                formKey={`${props.key}-${index}`}
                                pc={pc}
                                index={index}
                                formPrefix={props.formPrefix}
                                typeOptions={typeOptions}
                                allOptions={props.options}
                                register={props.register}
                                errors={props.errors}
                                setValue={props.setValue}
                                onEditClick={onEditClick}
                                handleRemoveProvision={handleRemoveProvision}
                                provLanguageEntry={provLanguage[index]}
                            />
                        ))}
                    </>
                )}
                <div style={{ marginTop: '2rem' }}>
                    <Button text="Add Provision" intent="primary" onClick={handleNewProvision} />
                </div>
            </div>
        </>
    );
};

export default ProvisionClause;



// WEBPACK FOOTER //
// ./src/components/common/ProvisionClause.tsx